import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { 
  ChevronLeft, 
  Download,
  CheckCircle,
  Shield,
  FileText,
  Mail,
  Star,
  Quote
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
import { LanguageSwitcher } from "./LanguageSwitcher";

interface WhitepaperThankYouPageProps {
  onNavigateBack: () => void;
  onNavigateToHome: () => void;
}

export function WhitepaperThankYouPage({ onNavigateBack, onNavigateToHome }: WhitepaperThankYouPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-white text-black">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-orange-500/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-black hover:text-orange-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-8 h-8 mr-2"
              />
              <span className="text-black text-lg">RebelGenomics</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher light />
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={onNavigateToHome}
              >
                EXPLORAR CURSOS
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Bloque 1 - Mensaje principal */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlbWFpbCUyMG5ld3NsZXR0ZXIlMjBzdWNjZXNzJTIwY29uZmlybWF0aW9ufGVufDF8fHx8MTc1NzY4ODIyNHww&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Email Newsletter Success Confirmation"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-white/80 to-white/90"></div>
        </motion.div>

        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <CheckCircle className="w-24 h-24 text-orange-500 mx-auto mb-8" />
              
              <h1 className="text-5xl md:text-7xl mb-8 text-black">
                ¡GRACIAS POR
                <br />
                <span className="text-orange-500">REGISTRARTE!</span>
              </h1>

              <p className="text-xl md:text-2xl mb-12 text-gray-700 leading-relaxed max-w-3xl mx-auto">
                Tu guía de introducción al cultivo in vitro ya está en camino a tu correo electrónico.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bloque 2 - Instrucciones */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl mb-8 text-orange-500">
                ¿QUÉ HACER AHORA?
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg text-center h-full">
                  <div className="text-4xl mb-4">👉</div>
                  <h3 className="text-2xl mb-4 text-orange-500">PASO 1</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    <strong>Revisa tu bandeja de entrada</strong> (y también la carpeta de spam/promociones).
                  </p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg text-center h-full">
                  <div className="text-4xl mb-4">📧</div>
                  <h3 className="text-2xl mb-4 text-orange-500">PASO 2</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    <strong>El remitente será Rebel Genomics.</strong> Busca nuestro email con tu guía adjunta.
                  </p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg text-center h-full">
                  <div className="text-4xl mb-4">⏱️</div>
                  <h3 className="text-2xl mb-4 text-orange-500">PASO 3</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    <strong>Si no recibes el correo</strong> en unos minutos, contáctanos en{" "}
                    <span className="text-orange-500">soporte@rebelgenomics.com</span>
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Bloque 3 - Imagen de recurso */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-4xl md:text-5xl mb-12 text-orange-500">
                TU GUÍA ESTÁ LISTA
              </h2>

              <Card className="bg-white border-orange-500/30 p-8 rounded-3xl shadow-2xl">
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1592496431122-2349e0fbc666?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib29rJTIwbW9ja3VwJTIwZ3VpZGUlMjBwZGYlMjAzZCUyMGJyb2NodXJlfGVufDF8fHx8MTc1NzY4ODIyNHww&ixlib=rb-4.1.0&q=80&w=1080"
                    alt="Book Mockup Guide PDF 3D Brochure"
                    className="w-full h-80 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl flex items-end justify-center p-6">
                    <div className="text-center">
                      <Download className="w-8 h-8 text-white mx-auto mb-2" />
                      <h3 className="text-xl text-white">En tu email ahora</h3>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <h3 className="text-2xl mb-2 text-orange-500">Guía de introducción al cultivo in vitro</h3>
                  <p className="text-gray-600 mb-4">5 páginas en castellano</p>
                  <div className="flex items-center justify-center space-x-6">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-orange-500 mr-2" />
                      <span className="text-gray-600">Formato PDF</span>
                    </div>
                    <div className="flex items-center">
                      <Download className="w-5 h-5 text-orange-500 mr-2" />
                      <span className="text-gray-600">Descarga directa</span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl mb-8 text-orange-500">
              LO QUE DICEN NUESTROS
              <br />
              <span className="text-black">ESTUDIANTES</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg h-full">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-orange-500 mb-4" />
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "La guía me dio las bases perfectas antes de tomar el curso completo. Muy clara y práctica, ideal para empezar."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-orange-600">MC</span>
                  </div>
                  <div>
                    <h4 className="text-black">María C.</h4>
                    <p className="text-gray-500 text-sm">Breeder, Valencia</p>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg h-full">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-orange-500 mb-4" />
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "Información muy valiosa y bien estructurada. Me ayudó a entender si el cultivo in vitro era para mi proyecto."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-orange-600">JR</span>
                  </div>
                  <div>
                    <h4 className="text-black">Javier R.</h4>
                    <p className="text-gray-500 text-sm">Técnico agrícola, Madrid</p>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white border-orange-500/20 p-8 rounded-3xl shadow-lg h-full">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-orange-500 mb-4" />
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "Contenido de calidad y ejemplos reales. Perfecto para comenzar en el mundo del cultivo in vitro sin conocimientos previos."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-orange-600">AL</span>
                  </div>
                  <div>
                    <h4 className="text-black">Ana L.</h4>
                    <p className="text-gray-500 text-sm">Startup biotecnológica, Barcelona</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bloque 4 - CTA secundario */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-600 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-6xl mb-8 text-white">
              ¿LISTO PARA EL
              <br />
              <span className="text-black">SIGUIENTE PASO?</span>
            </h2>
            
            <p className="text-xl md:text-2xl mb-12 text-white/90 leading-relaxed">
              Ahora que tienes los fundamentos, da el salto a la formación completa con nuestros cursos profesionales.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="bg-white hover:bg-gray-100 text-orange-600 text-xl px-12 py-6 rounded-full"
                onClick={onNavigateToHome}
              >
                EXPLORA NUESTROS CURSOS ONLINE
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-orange-600 text-xl px-12 py-6 rounded-full"
              >
                CURSO PRESENCIAL
              </Button>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <h3 className="text-xl mb-2">Curso Introducción</h3>
                <p className="text-white/80 text-sm">5 horas online • 299€</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <h3 className="text-xl mb-2">Curso Avanzado</h3>
                <p className="text-white/80 text-sm">5 horas online • 399€</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <h3 className="text-xl mb-2">Presencial</h3>
                <p className="text-white/80 text-sm">7 horas laboratorio • 1.499€</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bloque 5 - Nota de confianza */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-orange-500 mr-3" />
              <h3 className="text-2xl text-black">Privacidad garantizada</h3>
            </div>
            
            <p className="text-gray-600 leading-relaxed">
              Tus datos están seguros con nosotros. Solo los usaremos para enviarte la guía y contenido formativo relacionado.
              Puedes cancelar tu suscripción en cualquier momento.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                <span className="text-sm text-gray-700">Sin spam garantizado</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                <span className="text-sm text-gray-700">Solo contenido técnico</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-orange-500 mr-2" />
                <span className="text-sm text-gray-700">Datos seguros</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}